/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import {
    Form as FormikForm,
    TextField,
    DateTimePickerField,
    CheckboxField,
    SelectField,
    AutocompleteField,
} from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import AppContext from "AppContext";
import { Chip } from "@material-ui/core";
import { hasElements, removeDuplicates } from "utils/arrays";
import EventView from "./EventView";
import useStyles from "./useStyles";
import { eventColors, eventPermissions, getDefaultEventColor, placeTypes } from "./utils";
import { MenuItem, Link } from "@material-ui/core";
import EventOutlined from "@material-ui/icons/EventOutlined";
import CircleIcon from "@material-ui/icons/FiberManualRecordSharp";
import AccessTimeIcon from "@material-ui/icons/AccessTimeOutlined";
import LanguageIcon from "@material-ui/icons/LanguageOutlined";
import HomeWorkIcon from "@material-ui/icons/HomeWorkOutlined";
import DescriptionIcon from "@material-ui/icons/DescriptionOutlined";
import PeopleAltIcon from "@material-ui/icons/PeopleAltOutlined";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import { getUnitsOfTime } from "utils/dateUtils";
import { hasPermission } from "utils/session";
import CalendarContext from "./CalendarContext";

function Event({ data, onChange, mode, onCancel, onSuccessCallback, viewModeActionHandlers, getInternalGuestCalendar }) {
    const classes = useStyles();
    const readOnly = mode === "view";
    const dispatch = useDispatch();
    
    const [enabledFields, setEnabledFields] = useState({
        place: false,
        description: false,
        permissions: false,
        externalGuestEmails: false,
    });
    const {
        api: {
            Calendar: { Events },
        },
    } = useContext(AppContext);

    const { user, resources } = useContext(CalendarContext);

    const validationSchema = Yup.lazy((values) => {
        const getPlaceValidator = () => {
            if (values.placeType) {
                switch (values.placeType) {
                    case "virtual":
                        return Yup.string().required("A valid URL is required here").url("A valid URL is required here");
                    case "physical":
                        return Yup.string().required("A physical address is required here");
                    default:
                        return Yup.string().nullable().notRequired();
                }
            }
            return Yup.string().nullable().notRequired();
        };
        return Yup.object({
            title: Yup.string().required("Required field"),
            start: Yup.date().required("Required field"),
            end: Yup.date().required("Required field"),
            placeType: Yup.string().nullable().notRequired(),
            place: getPlaceValidator(),
        });
    });

    function onSuccess(sentPayload, setSubmitting) {
        setSubmitting(false);
        const performedAction = mode === "add" ? "created" : "updated";
        dispatch(notifySuccess(`Event ${performedAction} successfully!`));
        onSuccessCallback && typeof onSuccessCallback === "function" && onSuccessCallback();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    const buildGuestEmailList = (values) => {
        const { internalGuests, externalGuestEmails } = values;
        const guestEmails = hasElements(internalGuests) ? internalGuests.map((x) => x.email) : [];
        if (hasElements(externalGuestEmails)) {
            guestEmails.push(...externalGuestEmails);
        }
        return removeDuplicates(guestEmails);
    };

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const {
            id,
            start,
            end,
            placeType,
            place,
            title,
            allDay,
            description,
            guestPermissionsInviteOthers,
            guestPermissionsSeeGuestList,
            guestPermissionsModify,
            eventColor,
            notificationInterval,
            notificationTimeUnit,
            moduleCode,
            fileCaseId,
            dependsOnField,
        } = values;

        const payload = {
            id: mode === "add" ? null : id,
            ownerUserId: mode === "add" ? user.id : null,
            title,
            allDay,
            startDateTime: start,
            endDateTime: end,
            timezone: null,
            place: !placeType || placeType === "none" ? null : place,
            placeType: placeType === "none" ? null : placeType,
            description,
            guestPermissionsInviteOthers,
            guestPermissionsSeeGuestList,
            guestPermissionsModify,
            guestEmails: buildGuestEmailList(values),
            eventColor,
            notification: `${notificationInterval}-${notificationTimeUnit}`,
            moduleCode,
            fileCaseId,
            dependsOnField,
        };

        const apiCall = mode === "add" ? Events.add : Events.update;

        setSubmitting(true);

        apiCall({
            payload,
            onSuccess: () => onSuccess(payload, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    async function onExternalEmailKeyDown(event, form) {
        const {
            values: { externalGuestEmails: value },
            setFieldValue,
            setFieldTouched,
            setFieldError,
        } = form;
        if (event.keyCode === 13) {
            event.preventDefault();
            const externalEmail = String(event.target.value).toLowerCase().trim();
            // Validate if its a valid email here, otherwise set the field error
            try {
                await setFieldTouched("externalEmail_Temp", true, false);
                await Yup.string().email("Enter a valid email").validate(externalEmail);
                const internalUser = resources.users.find(
                    (x) => String(x.email).toLowerCase().trim() === String(externalEmail).toLowerCase().trim()
                );
                if (internalUser) {
                    throw new Error("There is an internal guest with this email");
                }
                const currentValue = hasElements(value) ? value : [];
                const newExternalEmails = currentValue.includes(externalEmail) ? currentValue : [...currentValue, externalEmail];
                setFieldValue("externalGuestEmails", newExternalEmails);
                setFieldValue("externalEmail_Temp", "");
                setFieldError("externalEmail_Temp", undefined);
            } catch (validationError) {
                setFieldError("externalEmail_Temp", validationError.message);
            }
        }
    }

    function handleDeleteExternalEmail(externalEmail, currentValue, setFieldValue) {
        const newExternalEmails = currentValue.filter((x) => x !== externalEmail);
        setFieldValue("externalGuestEmails", newExternalEmails);
    }

    const onFieldChange = (fieldName, value, form) => {
        onChange({ ...form.values, [fieldName]: value });

        if (fieldName === "guestPermissionsModify" && Boolean(value)) {
            form.setFieldValue("guestPermissionsInviteOthers", true);
            form.setFieldValue("guestPermissionsSeeGuestList", true);
        }

        if (fieldName === "guestPermissionsInviteOthers" && Boolean(value)) {
            form.setFieldValue("guestPermissionsSeeGuestList", true);
        }
    };

    const onInternalGuestsChange = ({ fieldName, value: guestUsers }) => {
        getInternalGuestCalendar(guestUsers);
    };

    const getGuestUsers = () => {
        if (hasElements(resources.users)) {
            if (mode === "add") {
                return resources.users.filter((x) => x.id !== user.id);
            }
            return resources.users;
        }
        return [];
    };

    if (!(data && user && hasElements(resources.users))) {
        return null;
    }

    const isCalendarAdmin = hasPermission(user, "calendar.admin");

    const isMine = mode === "add" || (user && data && user.id === data.ownerUserId);
    const canFullEditEvent = isMine || (isCalendarAdmin && data.guestPermissionsModify);
    const canOnlyEditGuests = data.guestPermissionsInviteOthers && !canFullEditEvent;

    if (readOnly) {
        const canEditEvent = canFullEditEvent || canOnlyEditGuests;
        const canDeleteEvent = isMine || isCalendarAdmin;
        const canSeeGuestList = canEditEvent || data.guestPermissionsSeeGuestList;
        const permissions = {
            isMine,
            canEditEvent,
            canDeleteEvent,
            canSeeGuestList,
        };
        return <EventView resources={resources} data={data} user={user} {...viewModeActionHandlers} {...permissions} />;
    }

    const getEventOwnerName = () => {
        if (mode === "add") {
            return user.name;
        }
        if (data.organizer) return data.organizer.displayName;
        if (resources && hasElements(resources.users))
            return resources.users.find((x) => x.id === data.ownerUserId)?.name || data.ownerUserId;
    };

    const getInitialValues = () => {
        const { guests } = data;
        const internalGuests = [];
        const externalGuestEmails = [];
        const eventColor = data.eventColor || getDefaultEventColor();
        if (hasElements(guests)) {
            for (const guest of guests) {
                const internalUser = resources.users.find((x) => x.email === guest.guestEmail);
                if (internalUser) {
                    internalGuests.push(internalUser);
                } else {
                    externalGuestEmails.push(guest.guestEmail);
                }
            }

            return { ...data, internalGuests, externalGuestEmails, eventColor };
        }

        return { ...data, eventColor };
    };

    const shouldDisplayField = (fieldName, form) => Boolean(enabledFields[fieldName]) || Boolean(form.values[fieldName]);

    const getEventColors = () => {
        const evtColors = eventColors;
        if (data.eventColor && !eventColors.some((x) => x.value === data.eventColor)) {
            evtColors.push({ value: data.eventColor, label: "Custom of Module" });
        }
        return evtColors;
    };

    return (
        <FormikForm
            id="in-dialog-form"
            onSubmit={onSubmitHandler}
            validateOnChange={false}
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onCancel={onCancel}
        >
            {(form) => (
                <Grid container spacing={2}>
                    <Grid item xs={1} />
                    <Grid item xs={11}>
                        <TextField
                            fieldName="title"
                            disabled={canOnlyEditGuests}
                            label=""
                            placeholder="Add title"
                            onChange={onFieldChange}
                            variant="standard"
                            size="medium"
                        />
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={11}>
                        <CheckboxField fieldName="allDay" label="All day" onChange={onFieldChange} disabled={canOnlyEditGuests} />
                    </Grid>
                    {!form.values.allDay && (
                        <>
                            <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                                <AccessTimeIcon color="action" />
                            </Grid>
                            <Grid item xs={6} className={classes.flexAlignItemsCenter}>
                                <DateTimePickerField
                                    fieldName="start"
                                    label="Start *"
                                    onChange={onFieldChange}
                                    disabled={canOnlyEditGuests}
                                    inputVariant="standard"
                                />
                            </Grid>
                            <Grid item xs={5} className={classes.flexAlignItemsCenter}>
                                <DateTimePickerField
                                    fieldName="end"
                                    label="End *"
                                    onChange={onFieldChange}
                                    disabled={canOnlyEditGuests}
                                    inputVariant="standard"
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                        {form.values.placeType === "virtual" ? <LanguageIcon color="action" /> : <HomeWorkIcon color="action" />}
                    </Grid>
                    {shouldDisplayField("place", form) ? (
                        <>
                            <Grid item xs={3}>
                                <SelectField
                                    fieldName="placeType"
                                    label="Place Type"
                                    options={placeTypes}
                                    disabled={canOnlyEditGuests}
                                    variant="standard"
                                />
                            </Grid>
                            {form.values.placeType && form.values.placeType !== "none" ? (
                                <Grid item xs={8}>
                                    <TextField
                                        fieldName="place"
                                        label="Place *"
                                        margin="dense"
                                        disabled={canOnlyEditGuests}
                                        variant="standard"
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={8} />
                            )}
                        </>
                    ) : (
                        <Grid item xs={11}>
                            Add a
                            <Link href="#" underline="hover" onClick={() => setEnabledFields((prev) => ({ ...prev, place: true }))}>
                                {" place"}
                            </Link>
                        </Grid>
                    )}
                    <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                        <DescriptionIcon color="action" />
                    </Grid>
                    <Grid item xs={11}>
                        {shouldDisplayField("description", form) ? (
                            <TextField
                                fieldName="description"
                                label="Description"
                                multiline
                                minRows={2}
                                disabled={canOnlyEditGuests}
                                variant="standard"
                            />
                        ) : (
                            <>
                                Add a
                                <Link
                                    href="#"
                                    underline="hover"
                                    onClick={() => setEnabledFields((prev) => ({ ...prev, description: true }))}
                                >
                                    {" description"}
                                </Link>
                            </>
                        )}
                    </Grid>
                    {resources && resources.users && (
                        <>
                            <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                                <PeopleAltIcon color="action" />
                            </Grid>
                            <Grid item xs={11}>
                                <AutocompleteField
                                    fieldName="internalGuests"
                                    multiple
                                    limitTags={2}
                                    options={getGuestUsers()}
                                    label="Guests"
                                    valueKey="id"
                                    labelKey="name"
                                    variant="standard"
                                    onChange={onInternalGuestsChange}
                                    renderOption={(option) => (
                                        <div className={classes.autocompleteOption}>
                                            <div className={classes.autocompleteOptionUserName}>
                                                <div>
                                                    <strong>{option.name}</strong>
                                                </div>
                                                <small className={classes.smallInfo}>{option.isAdmin ? "admin" : ""}</small>
                                            </div>
                                            <small>{option.email}</small>
                                        </div>
                                    )}
                                    getOptionLabel={(option) => {
                                        if (option) {
                                            return option.name;
                                        }
                                        return "";
                                    }}
                                />
                            </Grid>
                            <>
                                {canFullEditEvent && enabledFields.permissions ? (
                                    eventPermissions.map(({ value, label }) => (
                                        <Fragment key={`event-permission-${value}`}>
                                            <Grid item xs={1} />
                                            <Grid item xs={11}>
                                                <CheckboxField fieldName={value} label={label} onChange={onFieldChange} />
                                            </Grid>
                                        </Fragment>
                                    ))
                                ) : (
                                    <>
                                        <Grid item xs={1} />
                                        <Grid item xs={11}>
                                            {eventPermissions
                                                .filter(({ value }) => Boolean(form.values[value]))
                                                .map((x) => x.label)
                                                .join(", ")}
                                            {". Or"}
                                            <Link
                                                href="#"
                                                underline="hover"
                                                onClick={() => setEnabledFields((prev) => ({ ...prev, permissions: true }))}
                                            >
                                                {" edit permissions"}
                                            </Link>
                                        </Grid>
                                    </>
                                )}
                            </>
                        </>
                    )}
                    {shouldDisplayField("externalGuestEmails", form) ? (
                        <>
                            <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                                <PeopleAltIcon color="action" />
                            </Grid>
                            <Grid item xs={11}>
                                <TextField
                                    fieldName="externalEmail_Temp"
                                    label="Type an email"
                                    variant="standard"
                                    onKeyDown={(e) => onExternalEmailKeyDown(e, form)}
                                />
                            </Grid>
                            <Grid item xs={1} />
                            <Grid item xs={11}>
                                {hasElements(form.values.externalGuestEmails) &&
                                    form.values.externalGuestEmails.map((obj) => (
                                        <Chip
                                            key={`chip-external-guest-email-${obj}`}
                                            variant="outlined"
                                            color="secondary"
                                            onDelete={() =>
                                                handleDeleteExternalEmail(obj, form.values.externalGuestEmails, form.setFieldValue)
                                            }
                                            label={obj}
                                            className={classes.externalEmailChip}
                                        />
                                    ))}
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                                <PeopleAltIcon color="action" />
                            </Grid>
                            <Grid item xs={11}>
                                Add
                                <Link
                                    href="#"
                                    underline="hover"
                                    onClick={() => setEnabledFields((prev) => ({ ...prev, externalGuestEmails: true }))}
                                >
                                    {" external guests"}
                                </Link>
                            </Grid>
                        </>
                    )}
                    <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                        <EventOutlined color="primary" />
                    </Grid>
                    <Grid item xs={9} className={classes.flexAlignItemsCenter}>
                        <strong>{getEventOwnerName()}</strong>
                    </Grid>
                    <Grid item xs={2} className={classes.flexAlignItemsCenter}>
                        <SelectField
                            fieldName="eventColor"
                            label=""
                            variant="standard"
                            onChange={onFieldChange}
                            options={getEventColors()}
                            renderOption={(option, index) => (
                                <MenuItem key={index} value={option.value} title={option.label}>
                                    <CircleIcon htmlColor={option.value} />
                                </MenuItem>
                            )}
                        />
                    </Grid>
                    <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                        <NotificationsActiveIcon />
                    </Grid>
                    <Grid item xs={3} className={classes.flexAlignItemsCenter}>
                        <TextField fieldName="notificationInterval" label="Interval" variant="standard" type="number" />
                    </Grid>
                    <Grid item xs={3} className={classes.flexAlignItemsCenter}>
                        <SelectField
                            fieldName="notificationTimeUnit"
                            label="Unit of time"
                            variant="standard"
                            onChange={onFieldChange}
                            options={getUnitsOfTime(false, "m,h,d,w")}
                        />
                    </Grid>
                </Grid>
            )}
        </FormikForm>
    );
}

export default Event;
